import SelectPicker from '@databases/field-picker/SelectPicker';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType, type SelectOption } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type MultiChange,
  MultiValueChangeType,
} from '@databases/utils/database/changesets';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent, useCallback, useState } from 'react';

import { type EditComponentProps } from './types';

const SelectValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.SELECT>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  addChangeToDraft,
  contentId,
  placeholder,
  appearance,
}) => {
  const canCreate = defaultStoreUtils.useSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, { contentId })
        : undefined;
    return database && database.operations.updateMetadata && !database.meta.isLocked;
  });

  const [inputValue, setInputValue] = useState<string[]>([...value]);

  const handleCreate = useCallback(
    (option: SelectOption, allSelectedValues: string[]) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addChangeToDraft!({
        type: DatabaseChangeType.SET_SELECT_OPTIONS,
        fieldId: field.id,
        newValue: [...field.settings.selectOptions, option],
        change: {
          type: MultiValueChangeType.APPEND,
          value: option,
        },
      });

      const addedValue = option.id ?? option.label;

      const delta: MultiChange<DatabaseFieldType.SELECT> = field.settings.multi
        ? {
            type: MultiValueChangeType.APPEND,
            value: addedValue,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [addedValue],
          };

      if (field.settings.multi) {
        setInputValue(allSelectedValues);
      } else {
        setInputValue([addedValue]);
      }

      onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta,
      });
    },
    [
      addChangeToDraft,
      field.id,
      field.settings.multi,
      field.settings.selectOptions,
      onChange,
    ]
  );

  const handleSelect = useCallback(
    (addedValue: string, allSelectedValues: string[]) => {
      const delta: MultiChange<DatabaseFieldType.SELECT> = field.settings.multi
        ? {
            type: MultiValueChangeType.APPEND,
            value: addedValue,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [addedValue],
          };

      if (field.settings.multi) {
        setInputValue(allSelectedValues);
      } else {
        setInputValue([addedValue]);
      }

      onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta,
      });
    },
    [onChange, field.settings.multi]
  );

  const handleRemove = useCallback(
    (removedValues: string[], allSelectedValues: string[]) => {
      setInputValue(allSelectedValues);

      onChange(allSelectedValues, {
        type: DatabaseFieldType.SELECT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: value.indexOf(removedValues[0]), // FIXME: index?
        },
      });
    },
    [onChange, value]
  );

  return (
    <SelectPicker
      value={inputValue}
      options={field.settings.selectOptions}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      isMulti={field.settings.multi}
      isCreatable={canCreate && !!addChangeToDraft}
      onCreate={handleCreate}
      onSelect={handleSelect}
      onRemove={handleRemove}
      placeholder={placeholder}
      appearance={appearance}
    />
  );
};

export default SelectValueEdit;
